import { Avatar } from "@admin/components/atoms/Avatar";
import { Badge } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import { DataTable } from "@admin/components/molecules/Table";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import {
  CompanyInfo,
  getAllCompanies,
  useCommonStore,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { AddNewCompanyModal } from "./components/Modals/AddNewCompanyModal";
import { EditCompanyStatusModal } from "./components/Modals/EditCompanyStatusModal";

export function AllCompaniesPage() {
  const { isSuperAdmin } = useCurrentUserGroups();
  const { setCompany, clear } = useCommonStore();
  const navigate = useNavigate();
  const [isAddNewCompanyModalOpen, setIsAddNewCompanyModalOpen] =
    useState(false);
  const { data, isPending } = useQuery({
    queryKey: ["companies"],
    queryFn: getAllCompanies,
    enabled: isSuperAdmin,
  });
  const [showInactiveCompanies, setShowInactiveCompanies] = useState(false);
  const [isEditCompanyStatusModalOpen, setIsEditCompanyStatusModalOpen] =
    useState(false);
  const [selectedCompany, setSelectedCompany] = useState<
    CompanyInfo | undefined
  >();
  if (!isSuperAdmin) return null;

  return (
    <>
      <DataTable
        loading={isPending}
        title="All Companies"
        enableSearch
        searchPlaceholder="Search Company Name or Code"
        header={
          <div className="flex flex-row gap-[30px]">
            <div className="flex items-center gap-2">
              <label className="switch switch-md">
                <span className="switch-label">Inactive Companies</span>
                <input
                  checked={showInactiveCompanies}
                  onChange={(e) => setShowInactiveCompanies((prev) => !prev)}
                  name="check"
                  type="checkbox"
                />
              </label>
            </div>
            <Button onClick={() => setIsAddNewCompanyModalOpen(true)}>
              <i className="ki-filled ki-plus-squared"></i>
              Add New Company
            </Button>
          </div>
        }
        columns={[
          {
            accessorKey: "companyName",
            header: "Name",
            enableSorting: true,
            cell({ row }) {
              return (
                <div className="flex items-center">
                  <Avatar
                    src={row.original.avatarPhotoPath}
                    alt={row.original.companyName}
                  />
                  <span className="ml-2">{row.original.companyName}</span>
                </div>
              );
            },
          },
          {
            accessorKey: "companyCode",
            header: "Code",
            enableSorting: true,
          },
          {
            accessorKey: "isDeleted",
            header: "Status",
            cell({ row }) {
              return (
                <Badge
                  className="cursor-pointer"
                  type={row.original.isDeleted ? "danger" : "success"}
                  size="lg"
                  onClick={() => {
                    setSelectedCompany(row.original);
                    setIsEditCompanyStatusModalOpen(true);
                  }}
                >
                  {row.original.isDeleted ? "Inactive" : "Active"}
                </Badge>
              );
            },
          },
          {
            id: "Actions",
            cell({ row }) {
              return (
                <Badge
                  className="cursor-pointer z-1000"
                  onClick={() => {
                    clear();
                    setCompany(row.original);
                    navigate(`/company/locations`);
                  }}
                  size="lg"
                >
                  Profile
                </Badge>
              );
            },
          },
        ]}
        data={data
          ?.filter((item) => item.isDeleted === showInactiveCompanies)
          .sort((a, b) => Number(a.companyCode) - Number(b.companyCode))}
      />

      <AddNewCompanyModal
        open={isAddNewCompanyModalOpen}
        onClose={() => setIsAddNewCompanyModalOpen(false)}
      />
      <EditCompanyStatusModal
        open={isEditCompanyStatusModalOpen}
        onClose={() => setIsEditCompanyStatusModalOpen(false)}
        companyInfo={selectedCompany}
      />
    </>
  );
}
